import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import {
  Calendar,
  ChevronRight,
  Cross,
  Envelope,
  ExternalLink,
  LayersFront,
  Phone,
  Reset,
  WhatsappChat,
} from '@factorialco/f0-react/icons/app'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import copy from 'copy-to-clipboard'
import {
  AtsCreatedApplicationsfromApplicationsFragment,
  AtsHiringRolesEnum,
  AtsJobApplicationHeaderFeatureAvailabilitiesFragment,
  AtsJobApplicationHeaderFeaturesFragment,
  AtsJobApplicationHeaderFragment,
  AtsJobApplicationHeaderParticipantFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import SafeLocalStorage from 'modules/core/lib/SafeLocalStorage'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { isAbsoluteURL } from 'modules/core/lib/isAbsoluteURL'
import app from 'modules/core/store/models/app'

import { phaseColor } from 'modules/ats/components/ApplicationPhaseTag'
import { DeleteApplicationConfirmModal } from 'modules/ats/components/DeleteApplicationConfirmModal/DeleteApplicationConfirmModal'
import { RejectApplicationsModal } from 'modules/ats/components/RejectApplicationsModal'
import { ScheduleInterview } from 'modules/ats/components/ScheduleInterview'
import { SendDiscAssessmentModal } from 'modules/ats/components/SendDiscAssessmentModal'
import { SendOfferLetterModal } from 'modules/ats/components/SendOfferLetterModal'
import { OfferLetterFromScratchModal } from 'modules/ats/components/SendOfferLetterModal/OfferLetterFromScratch'
import { OfferLetterFromTemplateModal } from 'modules/ats/components/SendOfferLetterModal/OfferLetterFromTemplate'
import { AddApplicationsToJobPostingModal } from 'modules/ats/components/applications/AddApplicationsToJobPostingModal'
import { TalentPoolConfirmationModal } from 'modules/ats/components/candidates/TalentPoolConfirmationModal'
import { useUpdateApplicationStatus } from 'modules/ats/hooks/applications/useUpdateApplicationStatus'
import { usePhoneCallAtsCandidate } from 'modules/ats/hooks/candidates/useCallFromMyPhone'
import { isPhoneCallPushEnabled } from 'modules/ats/lib/phoneCallPush'
import { isAssessmentStage, isOfferStage } from 'modules/ats/lib/utils/applicationPhase'
import { isHiredStage } from 'modules/ats/lib/utils/applicationPhase'
import { ellipsisCandidateData } from 'modules/ats/lib/utils/candidate'
import { useUpdateTalentPool } from 'modules/ats/lib/utils/useUpdateTalentPool'
import { EditCandidateModal } from 'modules/ats/pages/Candidates/ShowCandidate/CandidateDetailPage/components/EditCandidateModal'
import { useGetMarketplaceCompanyInstallations } from 'modules/marketplace/hooks/integrations/companyInstallations/useGetMarketplaceCompanyInstallations'
import {
  IDEALDISC_INTEGRATION_ID,
  WHATSAPP_INTEGRATION_ID,
} from 'modules/marketplace/lib/integrationIds'

import { MoveToPhaseModal } from './MoveToPhaseModal'
import { useParticipantNotifications } from './useParticipantNotifications'

interface Props {
  applicationData: AtsJobApplicationHeaderFragment
  features: AtsJobApplicationHeaderFeaturesFragment
  featureAvailabilities: AtsJobApplicationHeaderFeatureAvailabilitiesFragment
  talentPoolByDefault: boolean
  participants: AtsJobApplicationHeaderParticipantFragment[]
  hasOfferLetter: boolean
  canCallCandidatePolicy: boolean
  hiringRole: AtsHiringRolesEnum
}

export const JobApplicationHeader: React.FC<Props> = ({
  applicationData,
  features,
  featureAvailabilities,
  talentPoolByDefault,
  participants,
  hasOfferLetter,
  canCallCandidatePolicy,
  hiringRole,
}) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { access, company } = useCurrentSession()

  const { data: companyInstallationsData } = useGetMarketplaceCompanyInstallations(
    {
      companyIds: [company.id],
    },
    false
  )

  const isWhatsappFeatureEnabled =
    features.enabledFeatures.nodes.find((n) => n.name === 'ats_whatsapp')?.enabled ?? false

  const isWhatsappInstalled = companyInstallationsData.some(
    (installation) => installation.integrationDatoId === WHATSAPP_INTEGRATION_ID
  )
  const isIdealDiscFeatureEnabled =
    features.enabledFeatures.nodes.find((n) => n.name === 'ats_ideal_disc_integration')?.enabled ??
    false

  const isIdealDiscInstalled = companyInstallationsData.some(
    (installation) => installation.integrationDatoId === IDEALDISC_INTEGRATION_ID
  )

  const isWhatsappEnabled = isWhatsappFeatureEnabled || isWhatsappInstalled

  const isIdealDiscEnabled = isIdealDiscFeatureEnabled || isIdealDiscInstalled

  const devOfferLetterTemplatesEnabled =
    features.enabledFeatures.nodes.find((node) => node.name === 'dev_ats_offer_letter_templates')
      ?.enabled ?? false

  const phoneCallPushFeature =
    features.enabledFeatures.nodes.find((n) => n.name === 'ats_phone_call_push')?.enabled ?? false

  const isScheduleInterviewAvailable =
    featureAvailabilities.featureAvailables.nodes.find((n) => n.name === 'interview_scheduling')
      ?.available ?? false
  const isSmartActionsAvailable =
    featureAvailabilities.featureAvailables.nodes.find((n) => n.name === 'smart_actions')
      ?.available ?? false

  const [openedModal, setOpenedModal] = useState<
    | 'move-to-phase'
    | 'reject'
    | 'schedule-an-interview'
    | 'ideal-disc-test'
    | 'add-to-job-posting'
    | 'delete'
    | 'send-offer-letter'
    | 'talent-pool'
    | 'edit-candidate'
    | 'send-offer-letter-from-template'
    | 'send-offer-letter-from-scratch'
    | undefined
  >(undefined)

  const {
    id,
    assessment,
    candidate,
    employee,
    jobPosting,
    photo,
    atsJobPostingTitle: title,
    qualified,
    rejectionReason,
    applicationPhase,
  } = applicationData
  const { email, phoneNumber, personalUrl, consentToTalentPool, talentPool } = candidate

  let personalUrlLink = null
  if (personalUrl) {
    personalUrlLink = isAbsoluteURL(personalUrl) ? personalUrl : `https://${personalUrl}`
  }

  const isHired = isHiredStage(applicationPhase.hiringStage?.name)

  const showSendOfferLetter = isOfferStage(applicationPhase.hiringStage?.name) && !hasOfferLetter
  const isEmployee = !!employee?.id || candidate.applications.some((a) => a.employee?.id)
  const showConvertToEmployee = qualified && isHired && !isEmployee
  const showDisqualify = (!isHired && qualified) ?? false
  const showReActivate = (!isHired && !qualified) ?? false
  const showDiscTest = isAssessmentStage(applicationPhase.hiringStage?.name) && isIdealDiscEnabled

  const isParticipant =
    participants?.some((participant) => participant.accessId === access.id) ?? false

  const copyUrl = async (url: string) => {
    if (copy(url)) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.copied'),
      })
    }
  }

  const onClickOpenWhatsappChat = () => {
    track(trackingEvents.ats.openWhatsappChatWithCandidate)
    window.open('https://wa.me/' + phoneNumber, '_blank', 'noopener,noreferrer')
  }

  const { updateApplicationStatus } = useUpdateApplicationStatus()

  const { createParticipant, deleteParticipant } = useParticipantNotifications(
    id,
    access.id,
    company.id
  )

  const { updateTalentPoolValue } = useUpdateTalentPool({
    candidateId: candidate.id,
    currentTalentPoolValue: talentPool,
  })

  const onClickTalentPool = async () => {
    const dontShowConfirmation =
      consentToTalentPool ||
      SafeLocalStorage.getItem(LOCAL_STORAGE_KEYS.atsTalentPoolConsentMessage, JSON.parse)

    if (talentPool || dontShowConfirmation) {
      await updateTalentPoolValue()
    } else {
      setOpenedModal('talent-pool')
    }
  }

  const { phoneCallAtsCandidate } = usePhoneCallAtsCandidate()

  const sendPhoneCallWithCandidate = async () => {
    track(trackingEvents.ats.sendPhoneCallWithCandidate)

    const response = await phoneCallAtsCandidate({
      atsCandidateId: candidate.id,
      accessId: access.id,
      atsApplicationId: id,
    })

    const result = handleResult(response, () => undefined)

    if (result.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('ats.application_details.basic_info.call_from_my_phone.success'),
        time: 2000,
      })
    }
  }

  return (
    <>
      <ResourceHeader
        title={candidate.fullName}
        description={title}
        avatar={{
          type: 'person',
          firstName: candidate.firstName,
          lastName: candidate.lastName,
          src: photo ?? undefined,
        }}
        primaryAction={{
          label: i18n.t('ats.application_details.move_to_phase.button'),
          icon: ChevronRight,
          onClick: () => setOpenedModal('move-to-phase'),
          isVisible: qualified ?? false,
        }}
        secondaryActions={[
          {
            label: i18n.t('ats.schedule_interview.title'),
            icon: Calendar,
            onClick: () => setOpenedModal('schedule-an-interview'),
            isVisible: isScheduleInterviewAvailable,
          },
          {
            label: i18n.t('ats.application_details.header.disqualify'),
            icon: Cross,
            onClick: () => setOpenedModal('reject'),
            isVisible: showDisqualify,
          },
          {
            label: i18n.t('ats.application_details.header.reactivate'),
            icon: Reset,
            onClick: () => updateApplicationStatus({ id, qualified: true }),
            isVisible: showReActivate,
          },
        ]}
        otherActions={[
          showConvertToEmployee && {
            label: i18n.t('ats.application_details.header.create_employee'),
            onClick: () => setTimeout(() => navigate(`${pathname}/convert-to-employee`), 500),
          },
          {
            label: i18n.t('ats.application_details.header.add_to_job_opening'),
            onClick: () => setOpenedModal('add-to-job-posting'),
          },
          showSendOfferLetter && {
            label: i18n.t('ats.application_details.header.send_offer_letter'),
            onClick: () => setOpenedModal('send-offer-letter'),
          },
          {
            label: i18n.t('ats.application_details.header.visit_candidate_profile'),
            onClick: () =>
              navigate(
                navigation.ats.candidates.id.applications.fullPath({
                  id: candidate.id,
                })
              ),
          },
          {
            label: i18n.t('ats.application_details.header.edit_candidate_profile'),
            onClick: () => setOpenedModal('edit-candidate'),
          },
          {
            label: isParticipant
              ? i18n.t('ats.application_details.header.notifications.mute')
              : i18n.t('ats.application_details.header.notifications.unmute'),
            onClick: () => (isParticipant ? deleteParticipant({}) : createParticipant({})),
          },
          showDiscTest && {
            label: !!assessment?.id
              ? i18n.t('ats.application_details.disc_test.resend_test')
              : i18n.t('ats.application_details.disc_test.send_test'),
            onClick: () => setOpenedModal('ideal-disc-test'),
          },
          {
            label: candidate.talentPool
              ? i18n.t('ats.application_details.header.remove_from_talent_pool')
              : i18n.t('ats.application_details.header.add_to_talent_pool'),
            onClick: () => onClickTalentPool(),
          },
          {
            label: i18n.t('ats.application_details.header.delete_application'),
            onClick: () => setOpenedModal('delete'),
            critical: true,
          },
        ].filter(Boolean)}
        status={
          qualified
            ? undefined
            : {
                label: i18n.t('ats.application_details.header.status_label'),
                text: !!rejectionReason?.reason
                  ? rejectionReason.reason
                  : i18n.t('ats.application_details.rejected_status'),
                variant: 'critical',
              }
        }
        metadata={[
          !!qualified && {
            label: i18n.t('ats.application_details.header.phase_label'),
            value: {
              type: 'dot-tag',
              label: applicationPhase.name,
              color: phaseColor(applicationPhase.hiringStage?.name || 'default'),
            },
          },
          !!email && {
            label: i18n.t('ats.application_details.header.email_label'),
            value: {
              type: 'text',
              content: ellipsisCandidateData(email) || '',
            },
            actions: [
              {
                label: i18n.t('ats.application_details.header.send_email'),
                icon: Envelope,
                onClick: () => window.open(`mailto:${email}`, '_blank', 'noopener,noreferrer'),
              },
              {
                label: i18n.t('common.copy'),
                icon: LayersFront,
                onClick: () => copyUrl(email),
              },
            ],
          },
          !!phoneNumber && {
            label: i18n.t('ats.application_details.header.phone_label'),
            value: { type: 'text', content: phoneNumber },
            actions: [
              !!isWhatsappEnabled && {
                label: i18n.t('ats.application_details.header.chat_on_whatsapp'),
                icon: WhatsappChat,
                onClick: () => onClickOpenWhatsappChat(),
              },
              isPhoneCallPushEnabled(canCallCandidatePolicy, phoneCallPushFeature) && {
                label: i18n.t('ats.application_details.header.call_from_my_phone'),
                icon: Phone,
                onClick: () => sendPhoneCallWithCandidate(),
              },
            ].filter(Boolean),
          },
          !!jobPosting.location?.city && {
            label: i18n.t('ats.application_details.header.location_label'),
            value: { type: 'text', content: jobPosting.location?.city },
          },
          !!personalUrlLink && {
            label: i18n.t('ats.application_details.header.link_label'),
            value: {
              type: 'text',
              content: ellipsisCandidateData(personalUrl) || '',
            },
            actions: [
              {
                label: i18n.t('ats.application_details.header.navigate_to_link'),
                icon: ExternalLink,
                onClick: () => window.open(personalUrlLink, '_blank', 'noopener,noreferrer'),
              },
              {
                label: i18n.t('common.copy'),
                icon: LayersFront,
                onClick: () => copyUrl(personalUrlLink),
              },
            ],
          },
        ]}
      />

      {openedModal === 'move-to-phase' && (
        <MoveToPhaseModal
          application={applicationData}
          hiringRole={hiringRole}
          isSmartActionsAvailable={isSmartActionsAvailable}
          onClose={() => setOpenedModal(undefined)}
          onOpenConvertToEmployeeModal={() => {
            if (isEmployee) return

            navigate(`${pathname}/convert-to-employee`)
          }}
        />
      )}

      {openedModal === 'reject' && (
        <RejectApplicationsModal
          jobPostingId={jobPosting.id}
          selectedApplications={[applicationData]}
          title={i18n.t('ats.candidates.applications.disqualify.title', {
            candidate_name: candidate?.fullName,
          })}
          onSuccess={() => setOpenedModal(undefined)}
          onClose={() => setOpenedModal(undefined)}
          saveToTalentPoolCheckboxValue={!!candidate?.consentToTalentPool && talentPoolByDefault}
          saveToTalentPoolCheckboxVisible={!!candidate?.consentToTalentPool}
        />
      )}

      {openedModal === 'schedule-an-interview' && (
        <ScheduleInterview
          application={applicationData}
          onClose={() => setOpenedModal(undefined)}
        />
      )}

      {openedModal === 'ideal-disc-test' && (
        <SendDiscAssessmentModal applicationId={id} onClose={() => setOpenedModal(undefined)} />
      )}

      {openedModal === 'send-offer-letter' &&
        // TO DO: show only SendOfferLetterModal when feature is ready
        (devOfferLetterTemplatesEnabled ? (
          <SendOfferLetterModal
            onSelectOption={(option) => {
              setOpenedModal(option)
            }}
            onClose={() => setOpenedModal(undefined)}
          />
        ) : (
          <OfferLetterFromScratchModal
            application={applicationData}
            onClose={() => setOpenedModal(undefined)}
          />
        ))}

      {openedModal === 'edit-candidate' && (
        <EditCandidateModal candidate={candidate} onClose={() => setOpenedModal(undefined)} />
      )}

      {openedModal === 'delete' && (
        <DeleteApplicationConfirmModal
          id={id}
          onCancel={() => setOpenedModal(undefined)}
          onSuccess={() => {
            navigate(
              navigation.ats.jobs.jobsId.applications.fullPath({
                jobId: jobPosting.id,
              })
            )
          }}
        />
      )}

      {openedModal === 'add-to-job-posting' && (
        <AddApplicationsToJobPostingModal
          applicationIds={[id]}
          onClose={() => setOpenedModal(undefined)}
          onSuccess={(applications: AtsCreatedApplicationsfromApplicationsFragment[]) => {
            const newApplication = applications[0]

            if (newApplication?.id) {
              navigate(
                navigation.ats.jobs.jobsId.applications.applicationDetail.fullPath({
                  jobId: newApplication.jobPosting.id,
                  application_id: newApplication.id,
                })
              )
            }
          }}
        />
      )}

      {openedModal === 'talent-pool' && (
        <TalentPoolConfirmationModal
          candidateId={candidate.id}
          onClose={() => setOpenedModal(undefined)}
        />
      )}

      {openedModal === 'send-offer-letter-from-scratch' && (
        <OfferLetterFromScratchModal
          application={applicationData}
          onClose={() => setOpenedModal(undefined)}
        />
      )}

      {openedModal === 'send-offer-letter-from-template' && (
        <OfferLetterFromTemplateModal
          jobApplicationId={id}
          onClose={() => setOpenedModal(undefined)}
        />
      )}
    </>
  )
}
